"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

/**
 * Mobile off-canvas menu (plain JS/JSX)
 * Fixes:
 * - Prevents global click-to-close from firing on inside clicks
 * - Cleans up scroll lock/backdrop on close and route changes
 * - Submenu can be expanded/collapsed reliably across multiple opens
 * - Avoids leaving panel inert / unclickable after first close
 */
export default function OffcanvasMobileMenu() {
  const pathname = usePathname();
  const [subOpen, setSubOpen] = useState(false);

  const isActive = (path) => pathname === path;
  const isParentActive = (paths) => paths.some((p) => pathname.startsWith(p));

  // Auto-open submenu when on an /earwax-removal* route
  useEffect(() => {
    setSubOpen(isParentActive(["/earwax-removal"]));
  }, [pathname]);

  const lockScroll = () => {
    document.body.classList.add("offcanvas-open");
    document.documentElement.classList.add("offcanvas-open");
  };

  const unlockScroll = () => {
    document.body.classList.remove("offcanvas-open");
    document.documentElement.classList.remove("offcanvas-open");
    document.body.style.removeProperty("overflow");
    document.documentElement.style.removeProperty("overflow");
    // Remove any leftover bootstrap-like backdrops
    document
      .querySelectorAll(".offcanvas-backdrop")
      .forEach((el) => el.remove());
  };

  const closeOffcanvas = () => {
    const panel = document.getElementById("menu-mobile");
    if (panel) {
      panel.classList.remove("show");
      panel.removeAttribute("aria-hidden");
      panel.style.pointerEvents = ""; // ensure clicks work next time
      // If you add your own backdrop elsewhere, also remove it here.
    }
    setSubOpen(false);
    unlockScroll();
  };

  // If your app toggles the drawer by adding/removing `.show`, this observer
  // ensures the panel is always interactive when opened and scroll is locked.
  useEffect(() => {
    const panel = document.getElementById("menu-mobile");
    if (!panel) return;

    const observer = new MutationObserver(() => {
      const open = panel.classList.contains("show");
      if (open) {
        panel.removeAttribute("inert");
        panel.style.pointerEvents = "auto";
        lockScroll();
      } else {
        panel.style.pointerEvents = "";
        unlockScroll();
      }
    });

    observer.observe(panel, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Close when clicking outside (backdrop/page), ignore clicks inside the drawer
  useEffect(() => {
    const onDocClick = (e) => {
      const panel = document.getElementById("menu-mobile");
      if (!panel || !panel.classList.contains("show")) return;
      if (panel.contains(e.target)) return; // inside -> ignore
      closeOffcanvas();
    };

    const onKey = (e) => {
      if (e.key === "Escape") closeOffcanvas();
    };

    document.addEventListener("click", onDocClick, true);
    document.addEventListener("touchstart", onDocClick, true);
    document.addEventListener("keydown", onKey, true);

    return () => {
      document.removeEventListener("click", onDocClick, true);
      document.removeEventListener("touchstart", onDocClick, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, []);

  const handleToggleSubmenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSubOpen((v) => !v);
  };

  return (
    <div
      className="offcanvas offcanvas-start mobile-nav-wrap"
      tabIndex={-1}
      id="menu-mobile"
      aria-labelledby="menu-mobile"
      aria-hidden="true"
      // prevent inside clicks from bubbling to any global closer
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="offcanvas-header top-nav-mobile">
        <div className="offcanvas-title">
          <Link href="/" onClick={closeOffcanvas}>
            <img
              src="/images/logo/Logo-pro-ear-clinic.svg"
              alt="Pro Ear Clinic"
            />
          </Link>
        </div>
        <button
          onClick={closeOffcanvas}
          aria-label="Close menu"
          className="p-2"
          type="button"
        >
          <i className="icon-close" />
        </button>
      </div>

      {/* Body */}
      <div className="offcanvas-body inner-mobile-nav">
        <div className="mb-body">
          <ul
            id="menu-mobile-menu"
            className="list-none divide-y divide-neutral-200
                       [&_a]:block [&_a]:px-6 [&_a]:py-4 [&_a]:no-underline
                       [&_a]:text-stone-800 hover:[&_a]:bg-neutral-50"
          >
            <li>
              <Link
                href="/"
                onClick={closeOffcanvas}
                aria-current={isActive("/") ? "page" : undefined}
                className={`text-lg font-semibold ${isActive("/") ? "text-teal-700" : ""}`}
              >
                Home
              </Link>
            </li>

            <li>
              <Link
                href="/about-us"
                onClick={closeOffcanvas}
                aria-current={isActive("/about-us") ? "page" : undefined}
                className={`text-lg font-semibold ${isActive("/about-us") ? "text-teal-700" : ""}`}
              >
                About
              </Link>
            </li>

            {/* Submenu: Ear Wax Removal */}
            <li>
              <button
                type="button"
                onClick={handleToggleSubmenu}
                onMouseDown={(e) => e.stopPropagation()}
                aria-expanded={subOpen}
                aria-controls="submenu-earwax"
                className={`w-full px-6 py-4 text-left text-2xl text-[#3c210e] font-semibold
                            flex items-center justify-between hover:bg-neutral-50
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600
                            ${isParentActive(["/earwax-removal"]) ? "text-teal-700" : ""}`}
              >
                <span>Ear Wax Removal</span>
                <svg
                  className={`h-4 w-4 shrink-0 text-stone-500 transition-transform ${subOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* overflow-hidden so submenu doesn't overlap siblings */}
              <div
                id="submenu-earwax"
                className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
                  subOpen ? "" : "max-h-0"
                }`}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <ul
                  className="space-y-1 py-2 pr-6
                             ml-6 pl-4 border-l border-neutral-200
                             [&_a]:px-0 [&_a]:py-2
                             [&_a]:text-base [&_a]:font-medium
                             hover:[&_a]:text-teal-700"
                >
                  <li>
                    <Link
                      href="/earwax-removal"
                      onClick={closeOffcanvas}
                      aria-current={
                        isActive("/earwax-removal") ? "page" : undefined
                      }
                      className={
                        isActive("/earwax-removal") ? "!text-teal-700" : ""
                      }
                    >
                      See all our services
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/earwax-removal/microsuction"
                      onClick={closeOffcanvas}
                      aria-current={
                        isActive("/earwax-removal/microsuction")
                          ? "page"
                          : undefined
                      }
                      className={
                        isActive("/earwax-removal/microsuction")
                          ? "!text-teal-700"
                          : ""
                      }
                    >
                      Microsuction
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/earwax-removal/water-irrigation"
                      onClick={closeOffcanvas}
                      aria-current={
                        isActive("/earwax-removal/water-irrigation")
                          ? "page"
                          : undefined
                      }
                      className={
                        isActive("/earwax-removal/water-irrigation")
                          ? "!text-teal-700"
                          : ""
                      }
                    >
                      Water Irrigation
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/earwax-removal/pre-hearing-aid-wax-removal"
                      onClick={closeOffcanvas}
                      aria-current={
                        isActive("/earwax-removal/pre-hearing-aid-wax-removal")
                          ? "page"
                          : undefined
                      }
                      className={
                        isActive("/earwax-removal/pre-hearing-aid-wax-removal")
                          ? "!text-teal-700"
                          : ""
                      }
                    >
                      Pre-Hearing Aid Wax Removal
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/earwax-removal/clinic-earwax-removal-bristol"
                      onClick={closeOffcanvas}
                      aria-current={
                        isActive(
                          "/earwax-removal/clinic-earwax-removal-bristol"
                        )
                          ? "page"
                          : undefined
                      }
                      className={
                        isActive(
                          "/earwax-removal/clinic-earwax-removal-bristol"
                        )
                          ? "!text-teal-700"
                          : ""
                      }
                    >
                      Clinic Earwax Removal
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/earwax-removal/home-visit-earwax-removal-bristol"
                      onClick={closeOffcanvas}
                      aria-current={
                        isActive(
                          "/earwax-removal/home-visit-earwax-removal-bristol"
                        )
                          ? "page"
                          : undefined
                      }
                      className={
                        isActive(
                          "/earwax-removal/home-visit-earwax-removal-bristol"
                        )
                          ? "!text-teal-700"
                          : ""
                      }
                    >
                      Home Visit Earwax Removal
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/earwax-removal/ear-health-assessment"
                      onClick={closeOffcanvas}
                      aria-current={
                        isActive("/earwax-removal/ear-health-assessment")
                          ? "page"
                          : undefined
                      }
                      className={
                        isActive("/earwax-removal/ear-health-assessment")
                          ? "!text-teal-700"
                          : ""
                      }
                    >
                      Ear Health Assessment
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/earwax-removal/childrens-earwax-removal-bristol"
                      onClick={closeOffcanvas}
                      aria-current={
                        isActive(
                          "/earwax-removal/childrens-earwax-removal-bristol"
                        )
                          ? "page"
                          : undefined
                      }
                      className={
                        isActive(
                          "/earwax-removal/childrens-earwax-removal-bristol"
                        )
                          ? "!text-teal-700"
                          : ""
                      }
                    >
                      Children’s Earwax Removal
                    </Link>
                  </li>
                </ul>
              </div>
            </li>

            {/* <li>
              <Link
                href="/blog"
                onClick={closeOffcanvas}
                aria-current={isActive("/blog") ? "page" : undefined}
                className={`text-lg font-semibold ${isActive("/blog") ? "!text-teal-700" : ""}`}
              >
                Blog
              </Link>
            </li> */}

            <li>
              <Link
                href="/contact-us"
                onClick={closeOffcanvas}
                aria-current={isActive("/contact-us") ? "page" : undefined}
                className={`text-lg font-semibold ${isActive("/contact-us") ? "!text-teal-700" : ""}`}
              >
                Contact
              </Link>
            </li>
          </ul>

          {/* Support */}
          <div className="support px-6 py-12">
            <Link href="/contact-us" className="text-need text-teal-700">
              Need help?
            </Link>
            <ul className="mb-info mt-2 space-y-1 text-stone-700">
              <li>
                Call Us Anytime: <span className="number">0117 441 0770</span>
              </li>
              <li>
                Email Us:{" "}
                <span className="mail">contact@proearclinic.co.uk</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
